import { CapacitorSQLite, SQLiteConnection, type SQLiteDBConnection } from '@capacitor-community/sqlite';
import type { ResourceDocument, SyncOperation } from '../domain/resources';
import { compareOperations, type LocalDatabase } from './local-database';

const DATABASE_NAME = 'jidelnickovac_local';

export class NativeSqliteAdapter implements LocalDatabase {
  readonly kind = 'native-sqlite' as const;
  private manager = new SQLiteConnection(CapacitorSQLite);
  private connection: SQLiteDBConnection | null = null;

  async initialize(): Promise<void> {
    const existing = await this.manager.isConnection(DATABASE_NAME, false);
    this.connection = existing.result
      ? await this.manager.retrieveConnection(DATABASE_NAME, false)
      : await this.manager.createConnection(DATABASE_NAME, false, 'no-encryption', 1, false);

    await this.connection.open();
    // journal_mode cannot be changed inside the transaction that execute()
    // starts by default (notably on iOS/SQLCipher).
    await this.connection.execute('PRAGMA journal_mode = WAL;', false);
    await this.connection.execute(`
      CREATE TABLE IF NOT EXISTS local_resources (
        type TEXT PRIMARY KEY NOT NULL,
        document_json TEXT NOT NULL,
        updated_at TEXT NOT NULL
      );
      CREATE TABLE IF NOT EXISTS sync_outbox (
        id TEXT PRIMARY KEY NOT NULL,
        operation_json TEXT NOT NULL,
        created_at TEXT NOT NULL
      );
      CREATE TABLE IF NOT EXISTS local_metadata (
        key TEXT PRIMARY KEY NOT NULL,
        value TEXT NOT NULL
      );
      PRAGMA user_version = 1;
    `);
  }

  async listResources(): Promise<ResourceDocument[]> {
    const result = await this.db().query('SELECT document_json FROM local_resources ORDER BY type');
    return (result.values || []).map(row => JSON.parse(String(row.document_json)) as ResourceDocument);
  }

  async putResource(resource: ResourceDocument): Promise<void> {
    await this.db().run(
      `INSERT INTO local_resources (type, document_json, updated_at)
       VALUES (?, ?, ?)
       ON CONFLICT(type) DO UPDATE SET
         document_json = excluded.document_json,
         updated_at = excluded.updated_at`,
      [resource.type, JSON.stringify(resource), resource.updatedAt]
    );
  }

  async commit(resource: ResourceDocument, operations: SyncOperation[]): Promise<void> {
    await this.db().executeTransaction([
      {
        statement: `INSERT INTO local_resources (type, document_json, updated_at)
          VALUES (?, ?, ?)
          ON CONFLICT(type) DO UPDATE SET
            document_json = excluded.document_json,
            updated_at = excluded.updated_at`,
        values: [resource.type, JSON.stringify(resource), resource.updatedAt]
      },
      ...operations.map(operation => ({
        statement: `INSERT INTO sync_outbox (id, operation_json, created_at)
          VALUES (?, ?, ?)
          ON CONFLICT(id) DO UPDATE SET
            operation_json = excluded.operation_json,
            created_at = excluded.created_at`,
        values: [operation.id, JSON.stringify(operation), operation.createdAt]
      }))
    ], true);
  }

  async listOperations(): Promise<SyncOperation[]> {
    const result = await this.db().query(
      'SELECT operation_json FROM sync_outbox ORDER BY created_at ASC'
    );
    return (result.values || [])
      .map(row => JSON.parse(String(row.operation_json)) as SyncOperation)
      .sort(compareOperations);
  }

  async putOperations(operations: SyncOperation[]): Promise<void> {
    for (const operation of operations) {
      await this.db().run(
        `INSERT INTO sync_outbox (id, operation_json, created_at)
         VALUES (?, ?, ?)
         ON CONFLICT(id) DO UPDATE SET
           operation_json = excluded.operation_json,
           created_at = excluded.created_at`,
        [operation.id, JSON.stringify(operation), operation.createdAt]
      );
    }
  }

  async deleteOperations(ids: string[]): Promise<void> {
    for (const id of ids) {
      await this.db().run('DELETE FROM sync_outbox WHERE id = ?', [id]);
    }
  }

  async getMetadata(key: string): Promise<string | null> {
    const result = await this.db().query(
      'SELECT value FROM local_metadata WHERE key = ? LIMIT 1',
      [key]
    );
    return result.values?.[0]?.value == null ? null : String(result.values[0].value);
  }

  async setMetadata(key: string, value: string): Promise<void> {
    await this.db().run(
      `INSERT INTO local_metadata (key, value)
       VALUES (?, ?)
       ON CONFLICT(key) DO UPDATE SET value = excluded.value`,
      [key, value]
    );
  }

  async flush(): Promise<void> {}

  private db(): SQLiteDBConnection {
    if (!this.connection) throw new Error('Native SQLite adapter is not initialized');
    return this.connection;
  }
}
